/**
 * The report's frames, read in the browser from the video the results page already plays: a hidden copy of the
 * player's <video> (same URL, same browser cache, so the person's video does not jump) seeks to each frame, the frame
 * and its marks (frameMarks.js) are drawn on a canvas and saved as a JPEG. The player loads the video with
 * crossOrigin="anonymous" (S3 allows it), else the browser would not let the page read the pixels.
 *
 * A seek reads the video from the keyframe before the frame (the backend's libx264 puts one at least every 250 frames),
 * not the whole file. On a slow link that takes long: the wait goes on while data arrives, and gives up only when no
 * data comes.
 */

const LIGHT_GREEN = '144, 215, 173';
const JPEG_QUALITY = 0.88;
// no new data for this long: the step failed
const TIMEOUT_MS = 15000;

/** Page 1's frame and page 2's frames, in picture pixels (backend HERO_PX, MOMENT_PX). */
export const HERO_WIDTH = 1800;
export const MOMENT_WIDTH = 1000;

const stopped = () => new DOMException('The report was stopped', 'AbortError');

// the next `event` of the video; an error, `ms` with no new data (each `progress` event starts the time again) or a
// stop rejects
const wait = (video, event, ms, signal) =>
	new Promise((resolve, reject) => {
		let timer;
		const done = (settle, value) => {
			clearTimeout(timer);
			video.removeEventListener(event, ok);
			video.removeEventListener('error', bad);
			video.removeEventListener('progress', alive);
			signal?.removeEventListener('abort', abort);
			settle(value);
		};
		const ok = () => done(resolve);
		const bad = () => done(reject, new Error('error'));
		const abort = () => done(reject, stopped());
		const alive = () => {
			clearTimeout(timer);
			timer = setTimeout(() => done(reject, new Error('timeout')), ms);
		};
		alive();
		video.addEventListener(event, ok);
		video.addEventListener('error', bad);
		video.addEventListener('progress', alive);
		signal?.addEventListener('abort', abort);
		if (signal?.aborted) abort();
	});

/** Draw the marks of one frame (frameMarks.marksAt) on a canvas context, in the backend report's order. */
export const paintMarks = (ctx, marks) => {
	const ring = (x, y, r) => {
		ctx.beginPath();
		ctx.arc(x, y, r, 0, Math.PI * 2);
		ctx.stroke();
	};
	ctx.save();
	ctx.lineCap = 'round';
	ctx.lineWidth = marks.lineWidth;
	marks.trails.forEach(({ rgb, alpha, segments }) => {
		ctx.strokeStyle = `rgba(${rgb}, ${alpha})`;
		ctx.beginPath();
		segments.forEach(([x1, y1, x2, y2]) => {
			ctx.moveTo(x1, y1);
			ctx.lineTo(x2, y2);
		});
		ctx.stroke();
	});
	if (marks.line) {
		const { x1, y1, x2, y2, dash, width } = marks.line;
		ctx.setLineDash(dash);
		ctx.lineCap = 'butt';
		ctx.lineWidth = width;
		ctx.strokeStyle = 'rgba(245, 245, 245, 0.95)';
		ctx.beginPath();
		ctx.moveTo(x1, y1);
		ctx.lineTo(x2, y2);
		ctx.stroke();
		ctx.setLineDash([]);
	}
	const r = marks.radius;
	ctx.lineWidth = Math.max(1, r / 4);
	ctx.strokeStyle = 'rgba(15, 15, 15, 0.55)';
	marks.dots.forEach(d => ring(d.x, d.y, r + 1));
	marks.dots.forEach(d => {
		ctx.fillStyle = `rgba(${d.rgb}, 0.75)`;
		ctx.beginPath();
		ctx.arc(d.x, d.y, r, 0, Math.PI * 2);
		ctx.fill();
	});
	ctx.lineWidth = Math.max(1.5, r / 3);
	ctx.strokeStyle = `rgba(${LIGHT_GREEN}, 0.95)`;
	marks.dots.filter(d => d.added).forEach(d => ring(d.x, d.y, r * 2.6));
	marks.pulses.forEach(p => {
		ctx.strokeStyle = `rgba(${LIGHT_GREEN}, ${p.alpha})`;
		ring(p.x, p.y, p.r);
	});
	ctx.restore();
};

/**
 * shots: [{ frame, width }] of the analysed clip (frame t plays at t / fps); marksFor(frame, width): its marks.
 * renewSrc: a new signed URL, asked once when the player's link no longer loads (a page open for hours).
 * onFrame(n): the capture now reads the n-th frame of the shots (1 to shots.length).
 * Returns { images: Map(frame → JPEG data URL, or null when the frame could not be read: the report says "Video
 * unavailable" there), failures: [reason] }.
 */
export const captureFrames = async ({
	src,
	shots,
	fps,
	marksFor,
	signal,
	renewSrc = null,
	onFrame = null,
	timeoutMs = TIMEOUT_MS,
	makeVideo = () => document.createElement('video'),
	makeCanvas = () => document.createElement('canvas'),
}) => {
	const images = new Map(shots.map(s => [s.frame, null]));
	const failures = [];
	const video = makeVideo();
	video.crossOrigin = 'anonymous';
	video.muted = true;
	video.preload = 'auto';
	video.playsInline = true;
	let current = src;
	const open = async url => {
		current = url;
		const loaded = wait(video, 'loadeddata', timeoutMs, signal);
		video.src = url;
		await loaded;
	};
	let renewed = !renewSrc;
	const renew = async () => {
		renewed = true;
		await open(await renewSrc());
	};
	// one canvas for every frame (a new size clears it), emptied at the end
	const canvas = makeCanvas();
	const grab = async ({ frame, width }) => {
		const seeked = wait(video, 'seeked', timeoutMs, signal);
		video.currentTime = (frame + 0.5) / fps;
		await seeked;
		canvas.width = width;
		canvas.height = Math.round((width * video.videoHeight) / video.videoWidth);
		const ctx = canvas.getContext('2d');
		ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
		paintMarks(ctx, marksFor(frame, width));
		return canvas.toDataURL('image/jpeg', JPEG_QUALITY);
	};
	const reason = err => (err?.name && err.name !== 'Error' ? err.name : err?.message);
	try {
		try {
			await open(src);
		} catch (err) {
			if (err?.name === 'AbortError' || renewed) throw err;
			await renew();
		}
		const order = [...shots].sort((a, b) => a.frame - b.frame);
		for (const [k, shot] of order.entries()) {
			if (signal?.aborted) throw stopped();
			onFrame?.(k + 1);
			try {
				images.set(shot.frame, await grab(shot));
			} catch (err) {
				if (err?.name === 'AbortError') throw err;
				// a tainted canvas taints every frame: stop there
				if (err?.name === 'SecurityError') {
					failures.push(`frame ${shot.frame}: SecurityError`);
					break;
				}
				// one more try on a new load of the video: a new link (once a report) for a video in error, the same
				// link when no data came (a second seek would wait behind the stuck request; a new load asks again)
				const stuck = err?.message === 'timeout';
				if (stuck || (!renewed && err?.message === 'error')) {
					try {
						await (stuck ? open(current) : renew());
						images.set(shot.frame, await grab(shot));
						continue;
					} catch (again) {
						if (again?.name === 'AbortError') throw again;
					}
				}
				failures.push(`frame ${shot.frame}: ${reason(err)}`);
				// a video in error after its one new link, or no data after a new load, does not recover: no waiting
				// for each frame left
				if (stuck || err?.message === 'error') break;
			}
		}
	} catch (err) {
		if (err?.name === 'AbortError') throw err;
		failures.push(`video: ${reason(err)}`);
	} finally {
		video.removeAttribute('src');
		video.load();
		canvas.width = 0;
		canvas.height = 0;
	}
	return { images, failures };
};
